import React, { useState, useEffect, useMemo, useRef } from "react";
import styles from "./style.scss";
import cn from "classnames";

interface Props {
  text?: string;
  content?: React.ReactNode;
  className?: string;
  slideLength?: string;
  position?: "top" | "right" | "bottom" | "left" | "rightTop" | "leftTop";
  noteWidth?: number | "auto";
  textAlign?: string;
  fadeout?: boolean;
  onFadeoutEnded?: () => void;
  children: React.ReactNode;
}

export const Note: React.FunctionComponent<Props> = ({
  children,
  content,
  text,
  className = "",
  slideLength = "0px",
  position = "top",
  noteWidth = "auto",
  fadeout = false,
  onFadeoutEnded
}) => {
  const slideLengthNumber = Number(slideLength.split("px")[0]);
  const childrenRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [noteWidthState, setNoteWidthState] = useState(
    noteWidth === "auto" ? 0 : noteWidth
  );

  // auto であれば、descriptionRefの幅を使用
  useEffect(() => {
    if (typeof noteWidth === "number") {
      setNoteWidthState(noteWidth);
    } else if (descriptionRef.current) {
      setNoteWidthState(descriptionRef.current.clientWidth);
    }
  }, [noteWidth, descriptionRef]);

  useEffect(() => {
    if (!childrenRef.current) return;
    const clientRects = childrenRef.current.getClientRects()[0];
    if (clientRects) {
      setWidth(clientRects.width);
    }
  }, [childrenRef]);

  const marginLeft = useMemo(() => {
    switch (position) {
      case "top":
      case "bottom":
        return `${-noteWidthState / 2 + slideLengthNumber}px`;
      case "left":
        return childrenRef.current ? `-${width / 2 + noteWidthState + 5}px` : 0;
      case "right":
        return childrenRef.current ? `${width / 2 + 5}px` : 0;
    }
  }, [position, slideLengthNumber, noteWidthState, width]);

  return (
    <div
      className={cn(styles.component, className, { [styles.fadeout]: fadeout })}
      onAnimationEnd={onFadeoutEnded}
      ref={childrenRef}
    >
      {children}
      <div
        data-testid="features-Note-note"
        className={cn(
          styles.note,
          { [styles.top]: position === "top" },
          { [styles.bottom]: position === "bottom" },
          { [styles.left]: position === "left" },
          { [styles.right]: position === "right" },
          { [styles.rightTop]: position === "rightTop" },
          { [styles.leftTop]: position === "leftTop" }
        )}
        style={{
          width: `${noteWidth}px`,
          marginLeft: marginLeft
        }}
      >
        <div className={styles.description} ref={descriptionRef}>
          {content}
          {text}
        </div>
      </div>
    </div>
  );
};
